import { AdminUTTOrders } from '../components/admin/AdminUTTOrders';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Users, TrendingUp, Wallet, Lock, RefreshCw, ArrowLeft,
  PieChart, BarChart3, Eye, EyeOff, ShieldCheck, Activity, AlertTriangle, CheckCircle2, Loader2,
  Smartphone, Send, MessageCircle, Copy, Download, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeCanvas } from 'qrcode.react';
import { AdminWhiTracker } from '../components/admin/AdminWhiTracker';
import { AdminWithdrawals } from '../components/admin/AdminWithdrawals';

const API = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'wekeza_admin_key';

const fmt = (n) => (n ?? 0).toLocaleString('en-US');
const tzs = (n) => `TZS ${Math.round(n || 0).toLocaleString('en-US')}`;

const StatCard = ({ label, value, sub, icon: Icon, color = '#106B3A' }) => (
  <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start justify-between mb-2">
      <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</span>
      <div className="w-9 h-9 rounded-xl flex items-center justify-center"
           style={{ background: `${color}1a`, color }}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <div className="text-3xl font-bold text-slate-900 leading-tight">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState(localStorage.getItem(STORAGE_KEY) || '');
  const [authed, setAuthed] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [overview, setOverview] = useState(null);
  const [stocks, setStocks] = useState(null);
  const [recent, setRecent] = useState(null);
  const [dseHealth, setDseHealth] = useState(null);
  const [faidaRegs, setFaidaRegs] = useState(null);
  const [faidaCompleting, setFaidaCompleting] = useState(null); // registration being completed
  const [faidaForm, setFaidaForm] = useState({ account_number: '', control_number: '', notes: '' });
  const [faidaSubmitting, setFaidaSubmitting] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testPushing, setTestPushing] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [waCta, setWaCta] = useState(null);
  const [waLang, setWaLang] = useState('sw');
  const [waForwarder, setWaForwarder] = useState(null);

  // Load WhatsApp CTA (business number, wa.me deep link) for the brochure QR tile
  useEffect(() => {
    if (!authed) return;
    (async () => {
      try {
        const [ctaRes, fwdRes] = await Promise.all([
          axios.get(`${API}/api/whatsapp/cta?lang=${waLang}`),
          axios.get(`${API}/api/whatsapp/forwarder-message?lang=${waLang}`),
        ]);
        setWaCta(ctaRes.data);
        setWaForwarder(fwdRes.data);
      } catch (e) {
        setWaCta(null);
        setWaForwarder(null);
      }
    })();
  }, [authed, waLang]);

  const downloadWaQr = () => {
    const canvas = document.getElementById('wa-cta-qr-canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = url;
    link.download = `wekeza-whatsapp-qr-${waLang}.png`;
    link.click();
  };

  const fetchAll = useCallback(async (key) => {
    setLoading(true);
    try {
      const headers = { 'X-Admin-Key': key };
      const [o, s, r, h, f] = await Promise.all([
        axios.get(`${API}/api/admin/analytics/overview`, { headers }),
        axios.get(`${API}/api/admin/analytics/stocks-held`, { headers }),
        axios.get(`${API}/api/admin/analytics/recent-users?limit=20`, { headers }),
        axios.get(`${API}/api/admin/dse-eod-health`, { headers }),
        axios.get(`${API}/api/admin/faida/registrations?limit=50`, { headers }).catch(() => ({ data: { count: 0, rows: [] } })),
      ]);
      setOverview(o.data);
      setStocks(s.data);
      setRecent(r.data);
      setDseHealth(h.data);
      setFaidaRegs(f.data);
      localStorage.setItem(STORAGE_KEY, key);
      setAuthed(true);
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'Invalid admin key' : 'Failed to load admin data');
      setAuthed(false);
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleForceDseScrape = async () => {
    if (scraping) return;
    setScraping(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/force-dse-scrape`, {}, {
        headers: { 'X-Admin-Key': adminKey },
      });
      toast.success(`DSE scrape OK: ${data.records_scraped} records, ${data.stocks_updated} stocks updated`);
      await fetchAll(adminKey);
    } catch (e) {
      toast.error(e.response?.data?.detail?.error || e.response?.data?.detail || 'DSE scrape failed');
    } finally {
      setScraping(false);
    }
  };

  const openFaidaComplete = (row) => {
    setFaidaCompleting(row);
    setFaidaForm({ account_number: '', control_number: '', notes: '' });
  };

  const submitFaidaComplete = async (e) => {
    e?.preventDefault?.();
    if (!faidaCompleting || faidaSubmitting) return;
    const payload = {
      account_number: (faidaForm.account_number || '').trim(),
      control_number: (faidaForm.control_number || '').trim(),
      notes: (faidaForm.notes || '').trim() || null,
    };
    if (!payload.account_number || !payload.control_number) {
      toast.error('Account Number and Control Number are required');
      return;
    }
    setFaidaSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/faida/registrations/${faidaCompleting.id}/complete`,
        payload,
        { headers: { 'X-Admin-Key': adminKey } },
      );
      toast.success(
        data.client_notification === 'whatsapp_sent'
          ? `Completed · Client auto-WhatsApp'd with their numbers`
          : `Completed · ${data.client_notification}`
      );
      setFaidaCompleting(null);
      await fetchAll(adminKey);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete registration');
    } finally {
      setFaidaSubmitting(false);
    }
  };


  const handleTestStkPush = async (e) => {
    e?.preventDefault?.();
    if (testPushing) return;
    // Normalize TZ phone same rules as backend
    const raw = String(testPhone || '').replace(/[^\d+]/g, '');
    let phone = raw;
    if (raw.startsWith('0') && raw.length === 10) phone = `+255${raw.slice(1)}`;
    else if (raw.startsWith('255') && raw.length === 12) phone = `+${raw}`;
    else if (raw.startsWith('7') && raw.length === 9) phone = `+255${raw}`;
    if (!phone.startsWith('+255') || phone.length !== 13) {
      toast.error('Enter a valid Tanzania mobile number (e.g. 0754710139)');
      return;
    }
    setTestPushing(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/selcom-test-stk`,
        { phone, note: 'admin_dashboard_regulator_demo' },
        { headers: { 'X-Admin-Key': adminKey } },
      );
      setTestResult(data);
      if (data.simulated) {
        toast.info('Selcom simulated mode on this env — order recorded, no real push sent.');
      } else if (data.ok) {
        toast.success(`STK push sent (order ${data.order_id}). Approve on your phone.`);
      } else {
        toast.warning(`Selcom returned resultcode ${data.resultcode ?? '?'}: ${data.message || 'no push initiated'}`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.selcom?.message || 'Test STK push failed');
      toast.error(msg);
      setTestResult({ ok: false, error: msg });
    } finally {
      setTestPushing(false);
    }
  };

  useEffect(() => {
    if (adminKey && !authed) fetchAll(adminKey);
  }, [adminKey, authed, fetchAll]);

  const handleAuth = (e) => {
    e.preventDefault();
    if (!adminKey.trim()) return;
    fetchAll(adminKey.trim());
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAdminKey('');
    setAuthed(false);
    setOverview(null);
    setStocks(null);
    setRecent(null);
    setDseHealth(null);
  };

  // ---------- Auth gate ----------
  if (!authed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-6">
        <form onSubmit={handleAuth} className="w-full max-w-sm bg-white rounded-3xl p-7 shadow-xl border border-slate-100">
          <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
            <ShieldCheck className="w-7 h-7 text-emerald-700" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Admin Portal</h1>
          <p className="text-sm text-slate-600 mt-1 mb-5">Enter the admin key to access Wekeza-Smart analytics.</p>
          <label className="block text-xs font-medium text-slate-700 mb-1.5">Admin key</label>
          <div className="relative">
            <input
              type={showKey ? 'text' : 'password'}
              value={adminKey}
              onChange={(e) => setAdminKey(e.target.value)}
              className="w-full h-12 px-4 pr-12 rounded-xl border border-slate-300 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100"
              placeholder="paste your ADMIN_SECRET"
              data-testid="admin-key-input"
              autoFocus
            />
            <button type="button" onClick={() => setShowKey(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    data-testid="admin-toggle-key-visibility">
              {showKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <button type="submit" disabled={loading || !adminKey.trim()}
                  className="w-full mt-5 h-12 rounded-xl bg-emerald-700 text-white font-semibold disabled:opacity-60"
                  data-testid="admin-login-btn">
            {loading ? 'Verifying…' : 'Unlock dashboard'}
          </button>
          <p className="text-xs text-slate-400 mt-4">
            The key is the value of the <code>ADMIN_SECRET</code> environment variable on this environment.
            Treat it like a master password.
          </p>
        </form>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/dashboard')} className="text-sm text-slate-600 flex items-center gap-1.5 hover:text-slate-900" data-testid="admin-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="font-semibold text-slate-900">Admin Portal</h1>
          <div className="flex items-center gap-2">
            <button onClick={() => fetchAll(adminKey)} disabled={loading}
                    className="text-slate-600 disabled:opacity-50" data-testid="admin-refresh-btn"
                    aria-label="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={handleLogout} className="text-xs text-slate-500 underline" data-testid="admin-logout-btn">
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-5">

        {/* DSE data-pipeline health */}
        {dseHealth && (
          (() => {
            const captured = dseHealth.captured ?? 0;
            const expected = dseHealth.expected ?? 28;
            const missing = dseHealth.missing_symbols || [];
            const healthy = missing.length === 0 && captured === expected;
            const stale = !dseHealth.trading_day || (() => {
              const today = new Date().toISOString().slice(0, 10);
              return dseHealth.trading_day !== today;
            })();
            const barColor = healthy ? '#106B3A' : (captured === 0 ? '#b91c1c' : '#b07208');
            const StatusIcon = healthy ? CheckCircle2 : AlertTriangle;
            return (
              <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="admin-dse-health-tile">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                      <Activity className="w-4 h-4 text-emerald-700" /> DSE data pipeline
                    </h2>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Latest snapshot: {dseHealth.latest_snapshot_at ? new Date(dseHealth.latest_snapshot_at).toLocaleString() : '—'}
                      {' · '}Trading day: {dseHealth.trading_day || '—'}
                    </p>
                  </div>
                  <button
                    onClick={handleForceDseScrape}
                    disabled={scraping}
                    className="text-xs font-medium px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-60 flex items-center gap-1.5"
                    data-testid="admin-force-scrape-btn"
                  >
                    {scraping
                      ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Scraping…</>
                      : <><RefreshCw className="w-3.5 h-3.5" /> Force scrape now</>}
                  </button>
                </div>

                <div className="flex items-baseline justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <StatusIcon className="w-5 h-5" style={{ color: barColor }} />
                    <span className="text-3xl font-bold text-slate-900" data-testid="admin-dse-captured">{captured}</span>
                    <span className="text-lg text-slate-500">/ {expected}</span>
                    <span className="text-xs text-slate-500 ml-1">symbols captured</span>
                  </div>
                  {stale && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Stale</span>
                  )}
                </div>

                <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${(captured / Math.max(expected, 1)) * 100}%`, background: barColor }}
                  />
                </div>

                {missing.length > 0 ? (
                  <p className="text-xs text-slate-600" data-testid="admin-dse-missing">
                    <span className="font-medium text-amber-800">Missing today:</span>{' '}
                    {missing.slice(0, 12).join(', ')}{missing.length > 12 ? `, +${missing.length - 12} more` : ''}
                  </p>
                ) : (
                  <p className="text-xs text-emerald-700 font-medium">All 28 symbols captured. Pipeline healthy.</p>
                )}
              </div>
            );
          })()
        )}

        {/* Headline stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            label="Registered users"
            value={fmt(overview?.users?.total)}
            sub={`+${fmt(overview?.users?.new_7_days)} this week · +${fmt(overview?.users?.new_30_days)} this month`}
            icon={Users}
          />
          <StatCard
            label="Total holdings"
            value={fmt(stocks?.totals?.total_holdings)}
            sub={`${fmt(stocks?.totals?.stock_holdings)} stock positions`}
            icon={PieChart}
            color="#1a3a6b"
          />
          <StatCard
            label="Transactions"
            value={fmt(overview?.transactions?.completed)}
            sub={`of ${fmt(overview?.transactions?.total)} total`}
            icon={Wallet}
            color="#b07208"
          />
          <StatCard
            label="Volume processed"
            value={tzs(overview?.transactions?.total_volume)}
            sub="completed transactions"
            icon={TrendingUp}
            color="#7c3aed"
          />
        </div>

        {/* Selcom test payment tile — TZS 500 STK push proof for regulators */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="admin-selcom-test-tile">
          <div className="flex items-start justify-between mb-1">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Send className="w-4 h-4 text-emerald-700" /> Selcom test payment
            </h2>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800">
              TZS 500 · Diagnostic
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Sends a real Selcom STK push to any Tanzanian mobile number so you can prove the gateway is live
            without touching the Premium subscription flow. Order prefix <code>WKZ-TEST-</code> is explicitly ignored by the activator.
          </p>

          <form onSubmit={handleTestStkPush} className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Smartphone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="0754 710 139"
                className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-300 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100 text-sm"
                data-testid="admin-test-stk-phone-input"
                required
              />
            </div>
            <button
              type="submit"
              disabled={testPushing || !testPhone.trim()}
              className="h-11 px-5 rounded-xl bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:opacity-60 flex items-center justify-center gap-2 shrink-0"
              data-testid="admin-test-stk-btn"
            >
              {testPushing
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                : <><Send className="w-4 h-4" /> Send TZS 500 STK push</>}
            </button>
          </form>

        </div>

        {/* WhatsApp AI CTA tile — printable QR + deep link for brochures / trade-fair posters */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="admin-whatsapp-cta-tile">
          <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-emerald-700" /> WhatsApp AI Coach — brochure QR
            </h2>
            <span
              className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                waCta?.using_sandbox
                  ? 'bg-sky-50 text-sky-800'
                  : 'bg-emerald-50 text-emerald-800'
              }`}
              data-testid="admin-wa-sender-badge"
            >
              {waCta?.using_sandbox ? '◆ Sandbox (fair mode)' : '● Business number'}
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            This is the correct QR + link for your printed brochures. Points clients to your Twilio-approved
            business number with a pre-filled prompt that triggers the AI coach on the first tap.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="p-3 bg-white rounded-2xl border border-slate-100 shadow-sm self-start">
              {waCta?.wa_url ? (
                <QRCodeCanvas
                  id="wa-cta-qr-canvas"
                  value={`${window.location.origin}/ai?lang=${waLang}`}
                  size={180}
                  level="M"
                  includeMargin
                  data-testid="admin-wa-cta-qr"
                />
              ) : (
                <div className="w-[180px] h-[180px] flex items-center justify-center text-xs text-slate-400">
                  Loading…
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0 space-y-3">
              <div className="flex gap-2">
                {['sw', 'en'].map(l => (
                  <button
                    key={l}
                    onClick={() => setWaLang(l)}
                    className={`px-3 h-8 rounded-lg text-xs font-medium border ${
                      waLang === l
                        ? 'bg-emerald-700 text-white border-emerald-700'
                        : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-600'
                    }`}
                    data-testid={`admin-wa-lang-${l}`}
                  >
                    {l === 'sw' ? 'Kiswahili' : 'English'}
                  </button>
                ))}
              </div>

              <div>
                <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1">Business number</div>
                <div className="text-sm font-mono text-slate-900" data-testid="admin-wa-number">
                  {waCta?.number || '—'}
                </div>
              </div>

              <div>
                <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1">Pre-filled message</div>
                <div className="text-xs text-slate-700 italic">"{waCta?.prefilled_text || '—'}"</div>
              </div>

              <div>
                <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1">Deep link (wa.me)</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[11px] bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 overflow-x-auto whitespace-nowrap" data-testid="admin-wa-url">
                    {waCta?.wa_url || '—'}
                  </code>
                  <button
                    onClick={() => {
                      if (waCta?.wa_url) {
                        navigator.clipboard.writeText(waCta.wa_url);
                        toast.success('Link copied');
                      }
                    }}
                    className="h-8 px-2 rounded-lg border border-slate-300 hover:border-emerald-600 text-slate-700 text-xs flex items-center gap-1 shrink-0"
                    data-testid="admin-wa-copy-btn"
                  >
                    <Copy className="w-3 h-3" /> Copy
                  </button>
                </div>
              </div>

              <div className="flex gap-2 pt-1 flex-wrap">
                <button
                  onClick={downloadWaQr}
                  disabled={!waCta?.wa_url}
                  className="h-10 px-4 rounded-xl bg-emerald-700 text-white text-sm font-semibold hover:bg-emerald-800 disabled:opacity-60 flex items-center gap-2"
                  data-testid="admin-wa-download-qr"
                >
                  <Download className="w-4 h-4" /> Download QR (PNG)
                </button>
                <a
                  href="/admin/brochure-stickers"
                  target="_blank"
                  rel="noreferrer"
                  className="h-10 px-4 rounded-xl bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 flex items-center gap-2"
                  data-testid="admin-wa-print-stickers"
                >
                  <Printer className="w-4 h-4" /> Print sticker sheet (24/page)
                </a>
                <a
                  href={waCta?.wa_url || '#'}
                  target="_blank"
                  rel="noreferrer"
                  className={`h-10 px-4 rounded-xl border text-sm font-semibold flex items-center gap-2 ${
                    waCta?.wa_url
                      ? 'border-slate-300 text-slate-700 hover:border-emerald-600'
                      : 'border-slate-200 text-slate-400 pointer-events-none'
                  }`}
                  data-testid="admin-wa-test-btn"
                >
                  <MessageCircle className="w-4 h-4" /> Test on my phone
                </a>
              </div>

              {waCta?.using_sandbox && waCta?.sandbox_join_code && (
                <div className="mt-2 flex items-start gap-2 text-xs text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-lg p-2">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    Sandbox flow ready. The QR pre-fills <code>join {waCta.sandbox_join_code}</code> so
                    a single tap enrols the client AND triggers the AI welcome. <strong>Print on the brochure:</strong>
                    <em> "Tap Send once — AI replies instantly."</em>
                  </span>
                </div>
              )}

              {waCta?.using_sandbox && !waCta?.sandbox_join_code && (
                <div className="mt-2 flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>
                    Sandbox is active but no join code is configured. Set <code>TWILIO_WHATSAPP_SANDBOX_JOIN_CODE</code>
                    in <code>backend/.env</code> or clients won't be able to text the bot.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Forwarder message tile — for the old brochure number to bounce clients to new AI number */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="admin-forwarder-tile">
          <div className="flex items-start justify-between mb-1 flex-wrap gap-2">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Send className="w-4 h-4 text-amber-600" /> Old-brochure forwarder message
            </h2>
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">
              Copy → paste on +255 754 710 139
            </span>
          </div>
          <p className="text-xs text-slate-500 mb-4">
            Whoever holds the personal number printed on the old brochure ({' '}
            <code>+255 754 710 139</code>) should copy this reply and paste it whenever a new client texts them.
            It bounces the client to the AI business number{' '}
            <code>{waForwarder?.business_number || '—'}</code>. Even better: set this as your WhatsApp Business
            "Away Message" for full automation.
          </p>

          <div className="relative">
            <textarea
              readOnly
              value={waForwarder?.message || 'Loading…'}
              rows={9}
              className="w-full font-mono text-xs bg-slate-50 border border-slate-200 rounded-xl p-3 resize-none"
              data-testid="admin-forwarder-textarea"
            />
            <button
              onClick={() => {
                if (waForwarder?.message) {
                  navigator.clipboard.writeText(waForwarder.message);
                  toast.success('Forwarder message copied');
                }
              }}
              className="absolute top-2 right-2 h-8 px-3 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-800 flex items-center gap-1"
              data-testid="admin-forwarder-copy"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
          </div>
        </div>

        {/* By asset type */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
          <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-emerald-700" /> Holdings by asset type
          </h2>
          <p className="text-xs text-slate-500 mb-4">Across the entire user base</p>
          {stocks?.by_asset_type?.length ? (
            <div className="space-y-2">
              {stocks.by_asset_type.map(row => {
                const max = Math.max(...stocks.by_asset_type.map(r => r.total_value || 0), 1);
                const pct = ((row.total_value || 0) / max) * 100;
                return (
                  <div key={row.asset_type} data-testid={`admin-type-row-${row.asset_type}`}>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-sm font-semibold text-slate-800 uppercase tracking-wide">{row.asset_type}</span>
                      <span className="text-xs text-slate-500">
                        {fmt(row.holder_count)} holders · {fmt(row.holdings)} positions · {tzs(row.total_value)}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full"
                           style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#106B3A,#1a8a4a)' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No holdings recorded yet.</p>
          )}
        </div>

        {/* Top stocks bought */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
          <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-700" /> Top stocks held
          </h2>
          <p className="text-xs text-slate-500 mb-4">Ranked by total invested value (DSE-listed positions)</p>
          {stocks?.stocks?.length ? (
            <div className="overflow-x-auto -mx-5 px-5">
              <table className="w-full text-sm">
                <thead className="text-slate-500 text-xs uppercase tracking-wide">
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2">#</th>
                    <th className="text-left py-2">Symbol</th>
                    <th className="text-right py-2">Holders</th>
                    <th className="text-right py-2">Total qty</th>
                    <th className="text-right py-2">Total value</th>
                  </tr>
                </thead>
                <tbody>
                  {stocks.stocks.map((s, i) => (
                    <tr key={s.symbol} className="border-b border-slate-100" data-testid={`admin-stock-row-${s.symbol}`}>
                      <td className="py-2 text-slate-400">{i + 1}</td>
                      <td className="py-2 font-semibold text-slate-900">{s.symbol}</td>
                      <td className="py-2 text-right text-slate-700">{fmt(s.holder_count)}</td>
                      <td className="py-2 text-right text-slate-700">{fmt(s.total_quantity)}</td>
                      <td className="py-2 text-right font-medium text-emerald-700">{tzs(s.total_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-500">No stock positions on the platform yet.</p>
          )}
        </div>

        {/* Recent users */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
          <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
            <Users className="w-4 h-4 text-emerald-700" /> Recent registrations
          </h2>
          <p className="text-xs text-slate-500 mb-4">Last {recent?.count ?? 0} sign-ups</p>
          {recent?.users?.length ? (
            <ul className="divide-y divide-slate-100 -mx-5">
              {recent.users.map(u => (
                <li key={u.id} className="px-5 py-2.5 flex items-center justify-between text-sm" data-testid={`admin-user-${u.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{u.name || u.email || u.phone || '—'}</div>
                    <div className="text-xs text-slate-500 truncate">{u.email || u.phone}</div>
                  </div>
                  <div className="text-xs text-slate-400 ml-3 flex-shrink-0">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">No users yet.</p>
          )}
        </div>

        {/* Faida Fund — WHI concierge registrations */}
        <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm" data-testid="admin-faida-tile">
          <h2 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-700" /> Faida Fund registrations
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            Leads submitted via WhatsApp / web → emailed to WHI. Enter the Account & Control numbers once WHI replies and the client is auto-notified on WhatsApp.
          </p>
          {faidaRegs?.rows?.length ? (
            <div className="overflow-x-auto -mx-5 px-5">
              <table className="w-full text-sm">
                <thead className="text-slate-500 text-xs uppercase tracking-wide">
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2">Client</th>
                    <th className="text-left py-2">Phone</th>
                    <th className="text-right py-2">Amount</th>
                    <th className="text-center py-2">Status</th>
                    <th className="text-right py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {faidaRegs.rows.map((row) => {
                    const pending = row.status === 'pending_whi_registration';
                    return (
                      <tr key={row.id} className="border-b border-slate-100" data-testid={`admin-faida-row-${row.id}`}>
                        <td className="py-2">
                          <div className="font-medium text-slate-900 truncate">{row.full_name || '—'}</div>
                          <div className="text-xs text-slate-500 truncate">{row.email || '—'}</div>
                        </td>
                        <td className="py-2 text-slate-700 whitespace-nowrap text-xs">
                          <div>{row.phone}</div>
                          <div className="text-slate-400">{row.source}</div>
                        </td>
                        <td className="py-2 text-right text-emerald-700 whitespace-nowrap">{tzs(row.desired_amount_tzs)}</td>
                        <td className="py-2 text-center">
                          {pending ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                              <AlertTriangle className="w-3 h-3" /> Pending WHI
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <CheckCircle2 className="w-3 h-3" /> Done
                            </span>
                          )}
                        </td>
                        <td className="py-2 text-right">
                          {pending ? (
                            <button
                              onClick={() => openFaidaComplete(row)}
                              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-700 text-white hover:bg-emerald-800"
                              data-testid={`admin-faida-complete-${row.id}`}
                            >
                              Complete
                            </button>
                          ) : (
                            <div className="text-xs text-slate-500 whitespace-nowrap">
                              <div>Acc: <code className="text-slate-700">{row.account_number}</code></div>
                              <div>Ctrl: <code className="text-slate-700">{row.control_number}</code></div>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-500">No Faida Fund registrations yet.</p>
          )}
        </div>

        <AdminWhiTracker adminKey={adminKey} />

        <AdminWithdrawals adminKey={adminKey} />

        <AdminUTTOrders adminKey={adminKey} />

        {/* Modal: complete a Faida registration */}
        {faidaCompleting && (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => !faidaSubmitting && setFaidaCompleting(null)} data-testid="admin-faida-modal">
            <form
              onSubmit={submitFaidaComplete}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
            >
              <h3 className="text-lg font-bold text-slate-900 mb-1">Complete Faida registration</h3>
              <p className="text-xs text-slate-500 mb-5">
                Client: <b>{faidaCompleting.full_name}</b> ({faidaCompleting.phone}) · Desired: {tzs(faidaCompleting.desired_amount_tzs)}
              </p>
              <label className="block text-xs font-medium text-slate-600 mb-1">Account Number (from WHI)</label>
              <input
                type="text"
                value={faidaForm.account_number}
                onChange={(e) => setFaidaForm({ ...faidaForm, account_number: e.target.value })}
                placeholder="e.g. WHI-ACC-012345"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 mb-3 text-sm"
                required
                data-testid="admin-faida-account-input"
              />
              <label className="block text-xs font-medium text-slate-600 mb-1">Control Number (GePG)</label>
              <input
                type="text"
                value={faidaForm.control_number}
                onChange={(e) => setFaidaForm({ ...faidaForm, control_number: e.target.value })}
                placeholder="e.g. 991234567890"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 mb-3 text-sm font-mono"
                required
                data-testid="admin-faida-control-input"
              />
              <label className="block text-xs font-medium text-slate-600 mb-1">Notes (optional)</label>
              <textarea
                value={faidaForm.notes}
                onChange={(e) => setFaidaForm({ ...faidaForm, notes: e.target.value })}
                rows={2}
                placeholder="Any extra info from WHI's response…"
                className="w-full border border-slate-300 rounded-lg px-3 py-2 mb-5 text-sm"
                data-testid="admin-faida-notes-input"
              />
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setFaidaCompleting(null)}
                  disabled={faidaSubmitting}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
                  data-testid="admin-faida-cancel"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={faidaSubmitting}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-700 text-white hover:bg-emerald-800 flex items-center gap-2"
                  data-testid="admin-faida-submit"
                >
                  {faidaSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save & WhatsApp client
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Deep link to investor-data analytics page */}
        <button
          onClick={() => navigate('/admin/analytics')}
          className="w-full bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between text-sm font-medium text-slate-800 hover:bg-slate-50"
          data-testid="admin-link-investor-analytics"
        >
          <span>Deep investor analytics (demographics · behaviour · sentiment · readiness)</span>
          <span className="text-emerald-700">Open →</span>
        </button>

        <p className="text-center text-xs text-slate-400 pt-2 pb-6">
          Admin data refreshes on demand · Wekeza-Smart · Confidential
        </p>
      </div>
    </div>
  );
}
